import React from 'react';
import { useSelector, shallowEqual } from 'react-redux';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { Container, useTheme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { translate } from 'translations/translator';
import { AppStoreState } from 'types';
import { getWorkbookTheme } from 'util/workbook-util';
import { getLicenseIsTrial } from 'state/modules/user/selectors/user-selectors';
import { HwtButton } from 'components/@common/Buttons/HwtButton';
const getTranslation = key => translate('lessonPlanner', key);

type Props = {
  handleStudentProgress: () => void;
  handleBlankAssignment: () => void;
};

export const LessonPlannerFooter: React.FC<Props> = ({
  handleStudentProgress,
  handleBlankAssignment
}) => {
  const { bookCode } = useParams();
  const theme = useTheme();
  const workbookTheme = useSelector(getWorkbookTheme(bookCode ?? ''));
  const location = useLocation();
  const navigate = useNavigate();
  const classes = useStyles(theme)(workbookTheme);
  const classrooms: any = useSelector(
    ({ entity }: AppStoreState) => entity.classrooms,
    shallowEqual
  );
  const onManageTeachingOrder = location.pathname.includes('manage-options');
  const user = useSelector(({ user }: AppStoreState) => user);
  const isValidTrialUser: boolean = useSelector(getLicenseIsTrial);
  const goBack = () => navigate(-1);

  const defaultButtonStyles: any = {
    width: '265px !important',
    textTransform: 'none',
    fontSize: '16px',
    padding: '10px',
    fontWeight: '500 !important'
  };
  const disable = user.isAuditor || isValidTrialUser || classrooms.length <= 0;
  return (
    <Container id={'actionBarGroup'} className={classes.container} maxWidth="xl" disableGutters>
      <div className={classes.buttonGroup}>
        <HwtButton
          disabled={disable}
          id={'viewStudentProgress'}
          variant="contained"
          fullWidth={true}
          style={
            disable
              ? { ...defaultButtonStyles }
              : { ...defaultButtonStyles, backgroundColor: '#000000' }
          }
          onClick={onManageTeachingOrder ? goBack : handleStudentProgress}
        >
          {onManageTeachingOrder ? getTranslation('back') : getTranslation('assignmentTracker')}
        </HwtButton>

        {!onManageTeachingOrder && (
          <HwtButton
            id={'createAssignment'}
            disabled={disable}
            variant="contained"
            fullWidth={true}
            onClick={handleBlankAssignment}
            style={{
              width: '210px'
            }}
          >
            {getTranslation('blankAssignment')}
          </HwtButton>
        )}
      </div>
    </Container>
  );
};

const useStyles = theme =>
  makeStyles(() => ({
    container: (workbookTheme: any) =>
      ({
        display: 'flex !important',
        flex: 1,
        justifyContent: 'space-between !important',
        padding: '8px 20px',
        position: 'sticky !important',
        bottom: 0,
        backgroundColor: `${workbookTheme?.primaryColor || theme.palette.link.main}`,
        '& .MuiButton-root': {
          '&:disabled': {
            border: 'none'
          }
        }
      } as any),
    buttonGroup: {
      display: 'flex',
      flex: 1,
      justifyContent: 'space-between'
    }
  }));
